import { lazy } from 'react'
import { LoaderFunctionArgs, Navigate, Outlet } from 'react-router-dom'
import loaderCommon from '@/lib/helpers/loaderCommon'
import { getCombinedOrganizationData } from '@/routes/settings/organization/users/actions'
import { getOrganizationStatusGroups } from '@/routes/settings/statuses/actions'
import { getOrganizationLabelGroups } from '@/routes/settings/labels/actions'
import { getProjectRoles } from '@/routes/settings/projectRoles/lib/loader'
import issueTypeLoader from '@/routes/settings/issueTypes/loader.ts'
import { loaderSingleProject } from '@/routes/settings/projectDetails/lib/actions.ts'

const OrganizationUsers = lazy(
  () => import('@/routes/settings/organization/users/view/organizationUsers.tsx'),
)
const OrganizationView = lazy(
  () => import('@/routes/settings/organization/information/view/organization.tsx'),
)
const ProtectedRoute = lazy(() => import('@/components/common/wrappers/ProtectedRoute.tsx'))
const ProjectDetails = lazy(
  () => import('@/routes/settings/projectDetails/view/ProjectDetails.tsx'),
)
const LabelsView = lazy(() => import('@/routes/settings/labels/view/Labels.tsx'))
const Projects = lazy(() => import('@/routes/settings/projects/view/Projects.tsx'))
const ProjectStageDetails = lazy(
  () => import('@/routes/settings/projectDetails/view/ProjectStageDetails.tsx'),
)
const StatusesView = lazy(() => import('@/routes/settings/statuses/view/statuses.tsx'))
const IssueTypes = lazy(() => import('@/routes/settings/issueTypes/view/IssueTypesView.tsx'))
const AccountView = lazy(() => import('@/routes/settings/account/view/account.tsx'))
const NotificationPreferencesView = lazy(
  () => import('@/routes/settings/notifications/view/NotificationPreferences.tsx'),
)
const PushDevicesView = lazy(() => import('@/routes/settings/pushDevices/view/PushDevicesView.tsx'))
const OfflineCacheView = lazy(
  () => import('@/routes/settings/offlineCache/view/OfflineCacheView.tsx'),
)
const ProjectRolesView = lazy(
  () => import('@/routes/settings/projectRoles/view/ProjectRolesView.tsx'),
)
const SettingsRoot = lazy(() => import('@/routes/settings/settingsRoot/SettingsRoot.tsx'))
const MapEditorPage = lazy(
  () => import('@/routes/settings/projectDetails/mapEditor/view/MapEditorPage.tsx'),
)

export const SETTINGS_ROUTES = {
  path: 'settings',
  element: <SettingsRoot />,
  children: [
    {
      index: true,
      element: <Navigate to="/settings/account" replace />,
    },
    {
      path: 'organization',
      element: <Outlet />,
      children: [
        {
          path: 'information',
          element: (
            <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
              <OrganizationView />
            </ProtectedRoute>
          ),
        },
        {
          path: 'users',
          element: (
            <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
              <OrganizationUsers />
            </ProtectedRoute>
          ),
          loader: () => loaderCommon(getCombinedOrganizationData),
        },
        {
          path: 'projects',
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Projects />,
            },
            {
              // Full-screen editor, deliberately a SIBLING of the
              // ProjectDetails subtree: ProjectStageDetails renders no
              // <Outlet/> (its children are path-matchers consumed by
              // ProjectDetails' pathname decider), and the editor needs the
              // full viewport, not the settings grid.
              path: ':projectId/project_stages/:projectStageId/map-editor',
              element: <MapEditorPage />,
              loader: (params: LoaderFunctionArgs) => loaderCommon(loaderSingleProject, params),
            },
            {
              path: ':projectId',
              element: <ProjectDetails />,
              loader: (params: LoaderFunctionArgs) => loaderCommon(loaderSingleProject, params),
              children: [
                // Project-scoped settings editors. The address now states the
                // scope the write actually uses: these editors set
                // `data.project` and delete `data.organization`, and no stage
                // foreign key exists anywhere in the schema.
                {
                  path: 'issue_types',
                  element: null,
                },
                {
                  path: 'labels',
                  element: null,
                },
                {
                  path: 'statuses',
                  element: null,
                },
                {
                  path: 'project-roles',
                  element: null,
                  loader: (params: LoaderFunctionArgs) => loaderCommon(getProjectRoles, params),
                },
                {
                  path: 'project_stages/:projectStageId',
                  loader: (params: LoaderFunctionArgs) => loaderCommon(loaderSingleProject, params),
                  element: <ProjectStageDetails />,
                  // LEGACY editor addresses. Nothing constructs these any
                  // more — they are kept matchable only so bookmarked URLs
                  // reach ProjectDetails, which redirects them to the
                  // project-scoped form. Do not build links to them.
                  children: [
                    {
                      path: 'issue_types',
                      element: null,
                    },
                    {
                      path: 'labels',
                      element: null,
                    },
                    {
                      path: 'statuses',
                      element: null,
                    },
                    {
                      path: 'project-roles',
                      element: null,
                      loader: (params: LoaderFunctionArgs) => loaderCommon(getProjectRoles, params),
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },

    {
      path: 'statuses',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <StatusesView />
        </ProtectedRoute>
      ),
      loader: () => loaderCommon(getOrganizationStatusGroups),
    },
    {
      path: 'labels',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <LabelsView />
        </ProtectedRoute>
      ),
      loader: () => loaderCommon(getOrganizationLabelGroups),
    },
    {
      path: 'issue_types',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <IssueTypes />
        </ProtectedRoute>
      ),
      loader: () => loaderCommon(issueTypeLoader),
    },
    {
      path: 'account',
      element: <AccountView />,
    },
    {
      path: 'notifications',
      element: <NotificationPreferencesView />,
    },
    {
      path: 'push-devices',
      element: <PushDevicesView />,
    },
    {
      path: 'offline-cache',
      element: <OfflineCacheView />,
    },

    {
      path: 'project-roles',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <ProjectRolesView />
        </ProtectedRoute>
      ),
      loader: (params: LoaderFunctionArgs) => loaderCommon(getProjectRoles, params),
    },
  ],
}
